/// <amd-module name="Wit/Common/WorkItemDialogs/Components/BulkEditDialog" />
import * as React from "react";
import { ITfsProjectService } from "TFS/Services/TfsProjectService";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { ListSelection } from "VSSUI/List";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { FieldValue } from "Wit/Common/WorkItemControls/WorkItemFieldControl";
import { BulkEditFieldsTable, Clause } from "Wit/Common/WorkItemDialogs/Components/BulkEditFieldsTable";
import { ProcessWorkItemsDialog, restoreSelection } from "Wit/Common/WorkItemDialogs/Components/ProcessWorkItemsDialog";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { HtmlEditor } from "Wit/HtmlEditor/Components/HtmlEditor";
import { INamedFieldDataDictionary, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IBulkEditDialogProps {
    fields: IFieldEntry[];
    onDismiss: () => void;
    onModifyItems?: (items: IWorkItem[]) => Promise<void> | void;
    selectedItems: INamedFieldDataDictionary[];
    getWorkItems?: (idsToUpdate: number[]) => IWorkItem[] | undefined;
    selection?: ListSelection;
    itemsPerPage?: number;
    allowTempIds?: boolean;
}

export interface IBulkEditDialogState {
    selectedTypeFields: IFieldEntry[];
    isValid: boolean;
    working: boolean;
    hasComments: boolean;
}

export class BulkEditDialog extends TfsComponent<IBulkEditDialogProps, IBulkEditDialogState> {
    private clauses = new ObservableArray<Clause>([
        {
            allowedValues: new ObservableArray([]),
            id: newGuid(),
            field: new ObservableValue<IFieldEntry | undefined>(undefined),
            value: new ObservableValue<FieldValue>("")
        }
    ]);
    private historyNotes = new ObservableValue("");
    private projectToTypes = new Map<string, Set<string>>();
    private allowedValuesProjectId = "";
    private useChangeIterationPerfomanceOptimization: boolean;

    constructor(props: IBulkEditDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            selectedTypeFields: [],
            isValid: false,
            working: true,
            hasComments: false
        };
        this.useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.UseChangeIterationPerfomanceOptimization,
            false
        );
    }

    public async componentDidMount(): Promise<void> {
        const projectsService = this.context.pageContext.getService<ITfsProjectService>("ITfsProjectService");
        const projects = await projectsService.getAllProjectsLoadedPromise();
        const projectNameToIdMap = new Map<string, string>();
        for (const project of projects) {
            projectNameToIdMap.set(project.name, project.id);
        }

        const importBulkEditFixEnabled = isFeatureFlagEnabled(
            this.context.pageContext,
            FeatureFlags.ImportBulkEditFixEnabled,
            false
        );

        // map all the projects and work item types
        for (const item of this.props.selectedItems) {
            let projectName = undefined;
            if (importBulkEditFixEnabled) {
                projectName = item[CoreFieldRefNames.TeamProject] ?? this.projectData?.name ?? "";
            } else {
                projectName = item[CoreFieldRefNames.TeamProject];
            }
            
            const type = item[CoreFieldRefNames.WorkItemType];
            const projectId = projectNameToIdMap.get(projectName);
            if (projectId) {
                const typesForProject = this.projectToTypes.get(projectId);
                if (typesForProject) {
                    typesForProject.add(type);
                } else {
                    this.projectToTypes.set(projectId, new Set([type]));
                }
            }
        }

        // find the project id we'll use to load special case allowed values - iteration path, area path, and tags
        const currentProjectId = this.projectData?.id;
        if (currentProjectId && this.projectToTypes.has(currentProjectId)) {
            this.allowedValuesProjectId = currentProjectId;
        } else if (this.projectToTypes.size > 0) {
            // fall back to the first project if none of the work items selected are in the current project
            this.allowedValuesProjectId = this.projectToTypes.keys().next().value;
        } else {
            this.allowedValuesProjectId = "";
        }

        // build a set of fields that are used by the selected work item types
        const workItemTypeDataPromises: Promise<IWorkItemTypeData[]>[] = [];
        const workItemTypeService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        for (const [projectId, workItemTypes] of this.projectToTypes) {
            workItemTypeDataPromises.push(workItemTypeService.getWorkItemTypeData(projectId, [...workItemTypes]));
        }

        // collect all the fields used by selected work item types
        const workItemTypeData = await Promise.all(workItemTypeDataPromises);
        const fieldIds = new Set(
            workItemTypeData
                .reduce((cumulativeList, currentList) => cumulativeList.concat(currentList), [])
                .map(typeData => typeData.fields)
                .reduce((cumulativeList, currentList) => cumulativeList.concat(currentList), [])
        );
        const selectedTypeFields = this.props.fields.filter(field => fieldIds.has(field.id));

        this.setState({ selectedTypeFields, working: false });
    }

    public render(): JSX.Element {
        const { onDismiss } = this.props;
        const { selectedTypeFields, isValid, working, hasComments } = this.state;
        const okEnabled = !working && (isValid || hasComments);
        return (
            <Dialog
                calloutContentClassName="work-item-dialog"
                titleProps={{ text: Resources.EditWorkItems }}
                onDismiss={onDismiss}
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        onClick: onDismiss
                    },
                    {
                        text: Resources.OK,
                        disabled: !okEnabled,
                        onClick: this.onSave,
                        primary: true
                    }
                ]}
                overlay={working ? { spinnerAriaLabel: Resources.UpdatingWorkItems } : undefined}
                resizable={true}
                showCloseButton={true}
            >
                <BulkEditFieldsTable
                    clauses={this.clauses}
                    fields={selectedTypeFields}
                    getAllowedValues={this.getAllowedValues}
                    onValidate={this.onValidate}
                    projectId={this.allowedValuesProjectId}
                />
                <HtmlEditor
                    className="history-input"
                    ariaLabel={Resources.EditWorkItemsNotesAriaLabel}
                    placeholder={Resources.EditWorkItemsNotes}
                    htmlContent={this.historyNotes.value}
                    onChange={(newValue: string) => {
                        this.historyNotes.value = newValue;

                        const hasComments = this.historyNotes.value?.length > 0;
                        this.setState({ hasComments });
                    }}
                    telemetryContext={{ controlName: "HtmlEditor" }}
                    showChromeBorder={true}
                />
            </Dialog>
        );
    }

    /**
     * Get allowed values for a field given the current selected items context (projects and work item types)
     */
    private getAllowedValues = async (field: IFieldEntry): Promise<string[] | undefined> => {
        // special case: Area path and iteration path values get handled/loaded by the control itself
        if (field.referenceName === CoreFieldRefNames.AreaPath || field.referenceName === CoreFieldRefNames.IterationPath) {
            return Promise.resolve([]);
        }

        if (field.type === FieldType.Boolean) {
            return Promise.resolve([Resources.TrueString, Resources.FalseString]);
        }

        const allowedValuesPromises: Promise<string[]>[] = [];
        for (const [projectId, workItemTypes] of this.projectToTypes) {
            allowedValuesPromises.push(this.getAllowedValuesForTypes(projectId, [...workItemTypes], field.referenceName));
        }

        const allowedValues = await Promise.all(allowedValuesPromises);
        const flattenedAllowedValues = allowedValues.reduce((cumulativeList, currentList) => cumulativeList.concat(currentList));
        return [...new Set(flattenedAllowedValues)].sort();
    };

    /**
     * Get the allowed values for a group of work item types in a project
     */
    private async getAllowedValuesForTypes(projectId: string, workItemTypeNames: string[], fieldRefName: string): Promise<string[]> {
        const fieldService = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
                ? this.context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
                : this.context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        const allowedValuesPromises: Promise<string[] | undefined>[] = [];
        for (const workItemTypeName of workItemTypeNames) {
            const allowedValues = fieldService.getAllowedValues(fieldRefName, projectId, workItemTypeName);
            allowedValuesPromises.push(allowedValues);
        }

        // flatten into a list of string allowed values
        const allowedValuesList = await Promise.all(allowedValuesPromises);
        return allowedValuesList.filter(v => v).reduce((cumulativeList, currentList) => cumulativeList!.concat(currentList!)) as string[];
    }

    private onSave = async () => {
        const okEnabled = this.state.isValid || this.state.hasComments;

        if (!okEnabled) {
            return;
        }

        const changesMap = new Map<string, IWorkItemFieldChange[]>();
        const changesList: IWorkItemFieldChange[] = [];

        if (this.state.isValid) {
            for (const clause of this.clauses.value) {
                if (clause.field.value) {
                    changesList.push({ fieldName: clause.field.value.referenceName, value: clause.value.value });
                }
            }
        }

        if (this.historyNotes.value !== "") {
            changesList.push({ fieldName: CoreFieldRefNames.History, value: this.historyNotes.value });
        }

        changesMap.set(BulkUpdateConstants.AllTypes, changesList);

        this.setState({ working: true });
        const idsToUpdate = this.props.selectedItems.map(fields => Number(fields[CoreFieldRefNames.Id])).filter(v => v);
        let selectedItems: INamedFieldDataDictionary[] = [];
        const selectionCopy = this.props.selection ? this.props.selection.value.map(item => ({ ...item })) : [];
        if (idsToUpdate.length > 0) {
            selectedItems = await new Promise(resolve => {
                this.openDialog(onDismiss => (
                    <ProcessWorkItemsDialog
                        onDismiss={() => {
                            onDismiss();
                            resolve(this.props.selectedItems);
                        }}
                        workItemIds={idsToUpdate}
                        processWorkItems={async workItemIds => {
                            const itemsToUpdate = this.props.getWorkItems && this.props.getWorkItems(workItemIds);
                            const bulkUpdateResult = await performBulkUpdate(this.context.pageContext, itemsToUpdate ?? workItemIds, changesMap);
                            this.setState({ working: false });
                            if (this.props.onModifyItems) {
                                await this.props.onModifyItems(bulkUpdateResult);
                            }
                            return true;
                        }}
                        maximumAmountOfSimultaneousRequests={1}
                        itemsPerStep={this.props.itemsPerPage}
                        callBack={() => {
                            if (this.props.selection && selectionCopy.length > 0) {
                                if (this.useChangeIterationPerfomanceOptimization) {
                                    restoreSelection(selectionCopy, this.props.selection);
                                } else {
                                    for (const range of selectionCopy) {
                                        this.props.selection.select(range.beginIndex, range.endIndex - range.beginIndex + 1);
                                    }
                                }
                            }
                        }}
                        allowTempIds={this.props.allowTempIds}
                    />
                ));
            });
        }

        this.props.onDismiss();
    };

    private openDialog = (callout: (onDismiss: () => void) => JSX.Element): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    };

    private onValidate = (isValid: boolean) => {
        if (isValid !== this.state.isValid) {
            this.setState({ isValid });
        }
    };
}
